import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot
} from "recharts";
import { RefreshCw, Droplets, Gauge, TrendingUp, Clock } from "lucide-react";
import FreshnessDot from "./FreshnessDot";
import { enrichWithAnchoredVwap } from "../utils/vwap";
import { usePremarketLevels } from "../hooks/usePremarketLevels";

const StockIndicatorsChart = ({ ticker, timeframe = '1d' }) => {
  const [chartData, setChartData] = useState([]);
  const [spyData, setSpyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentValues, setCurrentValues] = useState({
    price: null,
    mfi: null,
    rsi: null
  });
  const [forecast, setForecast] = useState(null);
  // Premarket H/L levels — drives the two white dotted reference lines below.
  // Hook returns null when not ready; lines are only rendered when ready=true.
  const { ready: pmReady, premarketHigh, premarketLow } = usePremarketLevels(ticker);

  const fetchIndicatorData = useCallback(async () => {
    if (!ticker) return;
    const LS_KEY = `sp_indicators_${ticker}_${timeframe}`;

    // Hydrate from cache instantly so the chart never shows red errors
    let hadCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      if (cached?.data?.chart_data) {
        setChartData(cached.data.chart_data);
        setCurrentValues(cached.data.current_values || {});
        setLastUpdate(new Date(cached.ts));
        hadCache = true;
      }
    } catch {}

    if (!hadCache) setLoading(true);
    setError(null);

    const TIMEOUTS = [30000, 45000, 60000];
    let lastErr = null;
    for (let attempt = 0; attempt < TIMEOUTS.length; attempt++) {
      try {
        const response = await axios.get(`${API}/stock/indicators-chart/${ticker}`, {
          params: { timeframe },
          timeout: TIMEOUTS[attempt],
        });
        if (response.data && response.data.chart_data) {
          setChartData(response.data.chart_data);
          setCurrentValues(response.data.current_values || {});
          setLastUpdate(new Date());
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
          setLoading(false);
          return;
        } else if (response.data?.error) {
          setError(response.data.error);
          setLoading(false);
          return;
        }
      } catch (err) {
        lastErr = err;
        if (attempt < TIMEOUTS.length - 1) {
          await new Promise(r => setTimeout(r, (attempt + 1) * 1500));
        }
      }
    }
    console.warn(`Indicators ${ticker}: silent fail, keeping cached data`, lastErr?.message);
    if (!hadCache) setError("Failed to load indicator data. Please try again.");
    setLoading(false);
  }, [ticker, timeframe]);

  // Fetch SPY (S&P 500 proxy) bars for the same timeframe so we can overlay
  // a normalized white line and compute period-over-period relative
  // performance.  Skip when the stock already IS SPY.
  const fetchSpyData = useCallback(async () => {
    if (!ticker || ticker.toUpperCase() === 'SPY') {
      setSpyData([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/stock/indicators-chart/SPY`, {
        params: { timeframe },
        timeout: 30000,
      });
      if (res.data?.chart_data) {
        setSpyData(res.data.chart_data);
      }
    } catch (_e) {
      setSpyData([]);
    }
  }, [ticker, timeframe]);

  // Fetch forecast + backtest stats for this timeframe (cyan dashed projection)
  const fetchForecast = useCallback(async () => {
    if (!ticker) return;
    try {
      const res = await axios.get(`${API}/stock/chart-forecast/${ticker}`, {
        params: { timeframe },
        timeout: 90000,
      });
      if (res.data && !res.data.error) {
        setForecast(res.data);
      } else {
        setForecast(null);
      }
    } catch (err) {
      setForecast(null);
    }
  }, [ticker, timeframe]);

  useEffect(() => {
    fetchIndicatorData();
    const interval = setInterval(fetchIndicatorData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchIndicatorData]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  useEffect(() => {
    fetchSpyData();
  }, [fetchSpyData]);

  const getSignalColor = (value, type) => {
    if (type === 'mfi') {
      if (value >= 80) return "#ef4444";
      if (value >= 70) return "#f97316";
      if (value >= 60) return "#22c55e";
      if (value >= 40) return "#6b7280";
      if (value >= 30) return "#f97316";
      return "#22c55e";
    } else if (type === 'rsi') {
      if (value >= 70) return "#ef4444";
      if (value >= 60) return "#f97316";
      if (value >= 40) return "#6b7280";
      if (value >= 30) return "#f97316";
      return "#22c55e";
    }
    return "#6b7280";
  };

  const getSignalText = (value, type) => {
    if (type === 'mfi') {
      if (value >= 80) return "EXTREMELY OVERBOUGHT";
      if (value >= 70) return "OVERBOUGHT";
      if (value >= 60) return "BULLISH";
      if (value >= 40) return "NEUTRAL";
      if (value >= 30) return "BEARISH";
      if (value >= 20) return "OVERSOLD";
      return "EXTREMELY OVERSOLD";
    } else if (type === 'rsi') {
      if (value >= 70) return "OVERBOUGHT";
      if (value >= 60) return "BULLISH";
      if (value >= 40) return "NEUTRAL";
      if (value >= 30) return "BEARISH";
      return "OVERSOLD";
    }
    return "N/A";
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-xs mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-300">{entry.name}:</span>
                <span className="font-bold" style={{ color: entry.color }}>
                  {(entry.name === 'Price' || entry.name === 'VWAP (anchored)' || entry.name === 'Regression' || entry.name === 'Forecast' || entry.name === 'S&P 500 (rebased)') ? `$${entry.value?.toFixed(2)}` : entry.value?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Compute regression line + support/resistance for price data, and
  // append forward FORECAST bars (cyan dashed projection) from the backend.
  // Also rebase SPY's price array to the stock's starting price so both
  // series share a single dollar Y-axis and visual divergence = alpha.
  const { enrichedData, supportPrice, resistancePrice, regressionSlope,
          forecastEndTime, forecastEndPrice, performance } = useMemo(() => {
    if (!chartData || chartData.length < 3) {
      return { enrichedData: chartData, supportPrice: null, resistancePrice: null,
               regressionSlope: 0, forecastEndTime: null, forecastEndPrice: null,
               performance: null };
    }
    
    const prices = chartData.map(d => d.price).filter(p => p > 0);
    if (prices.length < 3) {
      return { enrichedData: chartData, supportPrice: null, resistancePrice: null,
               regressionSlope: 0, forecastEndTime: null, forecastEndPrice: null,
               performance: null };
    }
    
    // Linear regression on price
    const n = prices.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    prices.forEach((p, i) => { sumX += i; sumY += p; sumXY += i * p; sumX2 += i * i; });
    const denom = (n * sumX2 - sumX * sumX);
    const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;
    
    // Support = min price, Resistance = max price
    const support = Math.min(...prices);
    const resistance = Math.max(...prices);
    
    // Rebase SPY: scale so SPY[0] == stock_first_price.  Index by `time` so
    // missing bars don't crash.  Both endpoints share the same timeframe so
    // the labels align in practice; we just key on time.
    const spyByTime = {};
    if (spyData && spyData.length > 0) {
      const spyPrices = spyData.map(d => d.price).filter(p => p > 0);
      if (spyPrices.length > 0) {
        const stockStart = prices[0];
        const spyStart   = spyPrices[0];
        const scale = stockStart / spyStart;
        spyData.forEach(d => {
          if (d.price > 0) spyByTime[d.time] = Math.round(d.price * scale * 100) / 100;
        });
      }
    }

    // Add regression value to each historical data point + per-bar VWAP + SPY
    const withVwap = enrichWithAnchoredVwap(chartData);
    let priceIdx = 0;
    const enriched = withVwap.map(d => {
      const spyVal = spyByTime[d.time] ?? null;
      if (d.price > 0) {
        const regVal = intercept + slope * priceIdx;
        priceIdx++;
        return { ...d, regression: Math.round(regVal * 100) / 100, forecast: null, spy: spyVal };
      }
      return { ...d, regression: null, forecast: null, spy: spyVal };
    });

    // Period performance: % change of both stock + SPY over the window.
    const stockStart = prices[0];
    const stockEnd   = prices[prices.length - 1];
    const stockPct   = ((stockEnd / stockStart - 1) * 100);
    let spyPct = null, diffPct = null;
    if (spyData && spyData.length > 0) {
      const spyPrices = spyData.map(d => d.price).filter(p => p > 0);
      if (spyPrices.length >= 2) {
        spyPct  = (spyPrices[spyPrices.length - 1] / spyPrices[0] - 1) * 100;
        diffPct = stockPct - spyPct;
      }
    }
    const perf = {
      stockStart, stockEnd,
      stockPct: Math.round(stockPct * 100) / 100,
      spyPct:   spyPct  != null ? Math.round(spyPct * 100) / 100 : null,
      diffPct:  diffPct != null ? Math.round(diffPct * 100) / 100 : null,
      spyStart: spyData?.[0]?.price ?? null,
      spyEnd:   spyData?.[spyData.length - 1]?.price ?? null,
    };

    // Append forward forecast bars from backend.
    // Anchor the forecast line to the last actual price (so it continues
    // visually from where we are now, not from the regression end-point).
    let lastPt = null;
    let fcEndTime = null;
    let fcEndPrice = null;
    if (forecast?.forecast_bars?.length > 0) {
      const lastActualPrice = prices[prices.length - 1];
      // Anchor: first item has forecast = lastActualPrice so the cyan line
      // visually joins the purple Price line.
      enriched[enriched.length - 1] = {
        ...enriched[enriched.length - 1],
        forecast: enriched[enriched.length - 1].price ?? lastActualPrice,
      };
      forecast.forecast_bars.forEach((fb) => {
        const pt = {
          time: fb.time,
          price: null,
          regression: null,
          forecast: fb.forecast_price,
          vwap: null,
          mfi: null,
          rsi: null,
          isForecast: true,
        };
        enriched.push(pt);
        lastPt = pt;
      });
      if (lastPt) {
        fcEndTime = lastPt.time;
        fcEndPrice = lastPt.forecast;
      }
    }
    
    return {
      enrichedData: enriched,
      supportPrice: support,
      resistancePrice: resistance,
      regressionSlope: slope,
      forecastEndTime: fcEndTime,
      forecastEndPrice: fcEndPrice,
      performance: perf,
    };
  }, [chartData, forecast, spyData]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">{ticker} Indicators</h3>
          <span className="text-xs text-gray-500">Price, MFI & RSI</span>
          <FreshnessDot timestamp={lastUpdate} size="sm" showLabel={true} />
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchIndicatorData}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs text-white transition-colors"
            data-testid="refresh-stock-indicators"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Current Values Cards */}
      <div className="grid grid-cols-3 gap-1 sm:gap-2">
        {/* Price Card */}
        <div className="bg-gradient-to-br from-purple-900/40 to-[#1a1a2e] border border-purple-700/50 rounded-lg p-2 sm:p-3" data-testid="stock-price-card">
          <div className="flex items-center gap-1 sm:gap-2 mb-1">
            <TrendingUp className="w-3 h-3 text-purple-400" />
            <span className="text-[9px] sm:text-[10px] text-purple-400 font-medium">PRICE</span>
          </div>
          <div className="text-base sm:text-xl font-bold text-purple-400">
            ${currentValues.price?.toFixed(2) || '--'}
          </div>
        </div>

        {/* MFI Card */}
        <div className="bg-gradient-to-br from-green-900/40 to-[#1a1a2e] border border-green-700/50 rounded-lg p-2 sm:p-3" data-testid="stock-mfi-card">
          <div className="flex items-center gap-1 sm:gap-2 mb-1">
            <Droplets className="w-3 h-3 text-green-400" />
            <span className="text-[9px] sm:text-[10px] text-green-400 font-medium">MFI</span>
          </div>
          <div className="text-base sm:text-xl font-bold" style={{ color: currentValues.mfi ? getSignalColor(currentValues.mfi, 'mfi') : '#6b7280' }}>
            {currentValues.mfi?.toFixed(2) || '--'}
          </div>
          <div className="text-[8px] sm:text-[9px] hidden sm:block" style={{ color: currentValues.mfi ? getSignalColor(currentValues.mfi, 'mfi') : '#6b7280' }}>
            {currentValues.mfi ? getSignalText(currentValues.mfi, 'mfi') : 'Loading...'}
          </div>
        </div>

        {/* RSI Card */}
        <div className="bg-gradient-to-br from-red-900/40 to-[#1a1a2e] border border-red-700/50 rounded-lg p-2 sm:p-3" data-testid="stock-rsi-card">
          <div className="flex items-center gap-1 sm:gap-2 mb-1">
            <Gauge className="w-3 h-3 text-red-400" />
            <span className="text-[9px] sm:text-[10px] text-red-400 font-medium">RSI</span>
          </div>
          <div className="text-base sm:text-xl font-bold" style={{ color: currentValues.rsi ? getSignalColor(currentValues.rsi, 'rsi') : '#6b7280' }}>
            {currentValues.rsi?.toFixed(2) || '--'}
          </div>
          <div className="text-[8px] sm:text-[9px] hidden sm:block" style={{ color: currentValues.rsi ? getSignalColor(currentValues.rsi, 'rsi') : '#6b7280' }}>
            {currentValues.rsi ? getSignalText(currentValues.rsi, 'rsi') : 'Loading...'}
          </div>
        </div>
      </div>

      {/* Stock vs S&P 500 performance box — period matches selected timeframe */}
      {performance && (
        <div
          data-testid="stock-vs-spy-perf"
          className="flex flex-wrap items-stretch gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-gradient-to-r from-slate-900/60 to-slate-950/60 text-xs"
        >
          <div className="flex flex-col min-w-[110px]" data-testid="perf-stock">
            <span className="text-[10px] uppercase tracking-wider text-purple-300 font-bold">
              {ticker} · {timeframe.toUpperCase()}
            </span>
            <span className={`font-mono text-base font-bold ${performance.stockPct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {performance.stockPct >= 0 ? '+' : ''}{performance.stockPct.toFixed(2)}%
            </span>
            <span className="text-[10px] font-mono text-slate-300">
              ${performance.stockStart.toFixed(2)} → ${performance.stockEnd.toFixed(2)}
            </span>
          </div>

          <div className="w-px bg-slate-700" />

          <div className="flex flex-col min-w-[110px]" data-testid="perf-spy">
            <span className="text-[10px] uppercase tracking-wider text-slate-100 font-bold">
              S&P 500 · {timeframe.toUpperCase()}
            </span>
            {performance.spyPct != null ? (
              <>
                <span className={`font-mono text-base font-bold ${performance.spyPct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {performance.spyPct >= 0 ? '+' : ''}{performance.spyPct.toFixed(2)}%
                </span>
                <span className="text-[10px] font-mono text-slate-300">
                  ${performance.spyStart?.toFixed(2)} → ${performance.spyEnd?.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="text-[11px] text-slate-400 italic">loading…</span>
            )}
          </div>

          <div className="w-px bg-slate-700" />

          <div
            className={`flex flex-col min-w-[140px] px-3 py-1 rounded ml-auto ${
              performance.diffPct == null
                ? 'bg-slate-800/40'
                : performance.diffPct >= 0
                ? 'bg-emerald-900/30 border border-emerald-600/50'
                : 'bg-rose-900/30 border border-rose-600/50'
            }`}
            data-testid="perf-diff"
          >
            <span className="text-[10px] uppercase tracking-wider text-white font-bold">
              {ticker} vs S&P 500
            </span>
            {performance.diffPct != null ? (
              <>
                <span className={`font-mono text-base font-bold ${performance.diffPct >= 0 ? 'text-emerald-200' : 'text-rose-200'}`}>
                  {performance.diffPct >= 0 ? '+' : ''}{performance.diffPct.toFixed(2)}%
                </span>
                <span className="text-[10px] text-slate-100">
                  {performance.diffPct >= 0 ? 'outperforming' : 'underperforming'} the index
                </span>
              </>
            ) : (
              <span className="text-[11px] text-slate-300 italic">need SPY data…</span>
            )}
          </div>
        </div>
      )}

      {/* Main Chart */}
      <div className="bg-[#111] border border-gray-800 rounded-lg p-2 sm:p-3" data-testid="stock-indicators-chart">
        {loading && chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading indicator data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-[300px] flex items-center justify-center" data-testid="indicators-skeleton">
            <div className="text-center max-w-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mb-2" />
              <p className="text-sm text-amber-300" data-testid="indicators-unavailable">Technical indicator data temporarily unavailable</p>
              <p className="text-[11px] text-gray-500 mt-1">{error}</p>
              <button data-testid="indicators-retry" onClick={() => fetchIndicatorData()} className="mt-3 px-3 py-1 rounded border border-gray-700 text-xs text-gray-300 hover:text-white hover:border-gray-500">Try again</button>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-gray-500 text-sm">No data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={enrichedData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="time" stroke="#666" tick={{ fill: '#888', fontSize: 9 }} />
              <YAxis yAxisId="price" domain={['auto', 'auto']} stroke="#666" tick={{ fill: '#888', fontSize: 9 }} tickFormatter={(value) => `$${value.toFixed(0)}`} />
              <YAxis yAxisId="indicators" orientation="right" domain={[0, 100]} stroke="#666" tick={{ fill: '#888', fontSize: 9 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
              <ReferenceLine yAxisId="indicators" y={70} stroke="#ef4444" strokeDasharray="5 5" />
              <ReferenceLine yAxisId="indicators" y={30} stroke="#22c55e" strokeDasharray="5 5" />
              {/* Support ZONE (green shaded area) */}
              {supportPrice && resistancePrice && (
                <ReferenceArea yAxisId="price" y1={supportPrice} y2={supportPrice - (resistancePrice - supportPrice) * 0.15}
                  fill="#22c55e" fillOpacity={0.1} stroke="none" />
              )}
              {supportPrice && (
                <ReferenceLine yAxisId="price" y={supportPrice} stroke="#22c55e" strokeDasharray="4 4" strokeWidth={2} strokeOpacity={0.9}
                  label={{ value: `srt $${supportPrice.toFixed(2)}`, position: 'left', fill: '#22c55e', fontSize: 10, fontWeight: 'bold' }} />
              )}
              {/* Resistance ZONE (red shaded area) */}
              {supportPrice && resistancePrice && (
                <ReferenceArea yAxisId="price" y1={resistancePrice} y2={resistancePrice + (resistancePrice - supportPrice) * 0.15}
                  fill="#ef4444" fillOpacity={0.1} stroke="none" />
              )}
              {resistancePrice && (
                <ReferenceLine yAxisId="price" y={resistancePrice} stroke="#ef4444" strokeDasharray="4 4" strokeWidth={2} strokeOpacity={0.9}
                  label={{ value: `rce $${resistancePrice.toFixed(2)}`, position: 'left', fill: '#ef4444', fontSize: 10, fontWeight: 'bold' }} />
              )}
              {/* Premarket High / Low — white dotted "ORB" reference lines so
                  intraday traders can spot current price vs the premarket
                  range at-a-glance. Only renders on 1d (intraday) timeframe. */}
              {pmReady && timeframe === '1d' && premarketHigh != null && (
                <ReferenceLine yAxisId="price" y={premarketHigh} stroke="#ffffff" strokeDasharray="2 4" strokeWidth={1.5} strokeOpacity={0.9}
                  ifOverflow="extendDomain"
                  label={{ value: `PM H $${premarketHigh}`, position: 'right', fill: '#ffffff', fontSize: 10 }} />
              )}
              {pmReady && timeframe === '1d' && premarketLow != null && (
                <ReferenceLine yAxisId="price" y={premarketLow} stroke="#ffffff" strokeDasharray="2 4" strokeWidth={1.5} strokeOpacity={0.9}
                  ifOverflow="extendDomain"
                  label={{ value: `PM L $${premarketLow}`, position: 'right', fill: '#ffffff', fontSize: 10 }} />
              )}
              <Line yAxisId="price" type="monotone" dataKey="price" name="Price" stroke="#a855f7" strokeWidth={2} dot={false} />
              {/* S&P 500 overlay — REBASED to stock's starting price so both
                  series share the same Y-axis. Pure white = high contrast. */}
              <Line yAxisId="price" type="monotone" dataKey="spy" name="S&P 500 (rebased)" stroke="#ffffff" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              {/* Anchored VWAP — violet per-bar line, accumulates from period start */}
              <Line yAxisId="price" type="monotone" dataKey="vwap" name="VWAP (anchored)" stroke="#ee82ee" strokeWidth={2.5} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
              {/* Regression line — orange dotted, thick and visible */}
              <Line yAxisId="price" type="monotone" dataKey="regression" name="Regression" stroke="#f97316" strokeWidth={3} strokeDasharray="5 5" dot={false} connectNulls />
              {/* FORECAST line — cyan dashed, projects forward for the full chart period */}
              <Line yAxisId="price" type="linear" dataKey="forecast" name="Forecast" stroke="#22d3ee" strokeWidth={2.5} strokeDasharray="6 4" strokeOpacity={0.95} dot={false} connectNulls isAnimationActive={false} />
              {/* Floating Est. Close $ label at the end of the forecast line */}
              {forecast && forecastEndTime && forecastEndPrice && (
                <ReferenceDot
                  yAxisId="price"
                  x={forecastEndTime}
                  y={forecastEndPrice}
                  r={4}
                  fill="#22d3ee"
                  stroke="#0e7490"
                  strokeWidth={1.5}
                  isFront={true}
                  label={{
                    value: (() => {
                      const conf = forecast.confidence_pct;
                      const band = forecast.confidence_band_pct;
                      if (conf !== undefined && conf !== null) {
                        return `Est: $${forecastEndPrice.toFixed(2)} • ${conf}% conf (±${band}%)`;
                      }
                      return `Est: $${forecastEndPrice.toFixed(2)}`;
                    })(),
                    position: 'left',
                    offset: 8,
                    fill: '#67e8f9',
                    fontSize: 10,
                    fontWeight: 'bold',
                  }}
                />
              )}
              <Line yAxisId="indicators" type="monotone" dataKey="mfi" name="MFI" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line yAxisId="indicators" type="monotone" dataKey="rsi" name="RSI" stroke="#ef4444" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Forecast Info Bar — above the legend */}
      {forecast && forecast.est_close_price && forecast.total_samples > 0 && (
        <div
          data-testid="forecast-info-bar"
          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-md border border-cyan-900/40 bg-gradient-to-r from-cyan-950/30 via-gray-900/40 to-gray-900/40 text-[11px]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-6 h-0.5 rounded" style={{ borderTop: '2.5px dashed #22d3ee' }}></span>
            <span className="text-cyan-300 font-semibold tracking-wide uppercase">{timeframe} Forecast</span>
            <span
              className={`px-1.5 py-0.5 rounded font-mono ${
                forecast.est_close_pct > 0.1
                  ? 'bg-green-900/50 text-green-300'
                  : forecast.est_close_pct < -0.1
                  ? 'bg-red-900/50 text-red-300'
                  : 'bg-gray-800 text-gray-300'
              }`}
            >
              {forecast.est_close_pct >= 0 ? '+' : ''}{forecast.est_close_pct.toFixed(2)}%
            </span>
            <span
              className="ml-1 px-2 py-0.5 rounded border border-green-800/60 bg-green-950/40 flex items-center gap-1"
              data-testid="forecast-est-close"
            >
              <span className="text-gray-400 uppercase tracking-wide text-[10px]">Est. Close:</span>
              <span className={`font-mono font-bold ${forecast.est_close_pct >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                ${forecast.est_close_price.toFixed(2)}
              </span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold ${
                  forecast.confidence_pct >= 70
                    ? 'bg-green-800/60 text-green-200'
                    : forecast.confidence_pct >= 40
                    ? 'bg-yellow-800/60 text-yellow-200'
                    : 'bg-red-800/60 text-red-200'
                }`}
                title={`${forecast.confidence_pct}% of backtested ${forecast.horizon_bars}-bar forecasts landed within ±${forecast.confidence_band_pct}% of projection over ${forecast.history_period} of history`}
              >
                {forecast.confidence_pct}% conf (±{forecast.confidence_band_pct}%)
              </span>
            </span>
          </div>

          <div className="flex items-center gap-2 text-gray-500">
            <span className="uppercase tracking-wide text-[10px]">{forecast.history_period} Backtest:</span>
            <span>
              Direction{' '}
              <span
                className={`font-mono font-bold ${
                  forecast.direction_accuracy_pct >= 55
                    ? 'text-green-400'
                    : forecast.direction_accuracy_pct >= 50
                    ? 'text-yellow-400'
                    : 'text-red-400'
                }`}
              >
                {forecast.direction_accuracy_pct}%
              </span>
            </span>
            <span className="text-gray-700">|</span>
            <span>
              Avg Err{' '}
              <span className="font-mono text-gray-300">±{forecast.avg_abs_error_pct}%</span>
            </span>
            <span className="text-gray-700">|</span>
            <span className="font-mono">{forecast.total_samples.toLocaleString()} samples</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <div className="bg-[#111] border border-gray-800 rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-purple-400 font-medium">Price</span>
          </div>
          <p className="text-gray-500">Stock price in USD</p>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-green-400 font-medium">MFI</span>
          </div>
          <p className="text-gray-500">Money Flow Index (0-100)</p>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 font-medium">RSI</span>
          </div>
          <p className="text-gray-500">Relative Strength Index (0-100)</p>
        </div>
      </div>
    </div>
  );
};

export default StockIndicatorsChart;
